/**
 * TranslationsPanel — the "Translations" tab inside the Edit Course Metadata
 * modal. This is the MANAGEMENT surface for a master course's language
 * versions: add a language, watch progress, retry failures, update stale
 * translations, delete a version.
 *
 * Switching BETWEEN languages is a separate concern and lives in the header's
 * language dropdown (navigation), not here (configuration).
 *
 * Rendered only for master courses (source_course_id == null) — translations
 * are always created from the original, never from another translation.
 *
 * Row states:
 *   IN_PROGRESS       → progress bar (modules / lessons / audio)
 *   COMPLETED         → Open
 *   COMPLETED + stale → "Out of date" + Re-translate
 *   PARTIALLY_FAILED  → failed count (expands to name the lessons) + Re-translate failed
 *   FAILED            → Re-translate failed
 */

import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { looksStalled } from '@/components/courses/TranslationProgressSection';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Clock,
  Globe,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import { getLanguageName, languageOptions } from '@/constants/languageOptions';
import {
  CourseTranslationSummary,
  describeTranslationTiming,
} from '@/services/course-translation.service';
import {
  useCourseTranslations,
  useDeleteTranslation,
  useRetryTranslation,
  useStartTranslation,
} from '@/hooks/useCourseTranslations';

interface TranslationsPanelProps {
  courseId: string;
  /** BCP-47 code of the master course, e.g. 'en-US' — excluded from the picker */
  sourceLanguageCode: string;
  /** Navigate to a translated course's edit screen */
  /**
   * Called with the LANGUAGE CODE, not translated_course_id. That id belongs to
   * a TranslatedPedagogicalCourse row, which the course endpoints do not serve
   * -- opening it returned 404. language=.
   */
  onOpenCourse: (languageCode: string) => void;
  /** Published courses have a FIXED set of languages: no new ones, no deletes. */
  isPublished?: boolean;
}

/** "Hindi (हिन्दी)", never "🇮🇳 Hindi (हिन्दी)" and never a bare "hi-IN". */
function languageLabel(code: string): string {
  return getLanguageName(code) || code;
}

function formatDate(iso?: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
}

export default function TranslationsPanel({
  courseId,
  sourceLanguageCode,
  onOpenCourse,
  isPublished = false,
}: TranslationsPanelProps) {
  const { data: translations, isLoading } = useCourseTranslations(courseId);
  const startMutation = useStartTranslation(courseId);
  const retryMutation = useRetryTranslation(courseId);
  const deleteMutation = useDeleteTranslation(courseId);

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [deletingLanguage, setDeletingLanguage] = useState<{
    code: string;
    name: string;
  } | null>(null);

  // One mutation object is shared by every row and remembers only the LAST
  // call, so starting a second language re-enabled the first one's button and
  // a repeat click reached the backend twice — a 409. Tracked per language.
  const [retryingCodes, setRetryingCodes] = useState<Set<string>>(new Set());

  const handleRetry = (languageCode: string, suppressToast = false) => {
    // Second click while this language is already in flight: ignore it.
    if (retryingCodes.has(languageCode)) return;

    setRetryingCodes((current) => new Set(current).add(languageCode));
    retryMutation.mutate({ language_code: languageCode, suppressToast }, {
      onSettled: () =>
        setRetryingCodes((current) => {
          const next = new Set(current);
          next.delete(languageCode);
          return next;
        }),
    });
  };

  const rows = translations ?? [];
  const staleCount = rows.filter((r) => r.is_stale).length;

  const existingCodes = useMemo(
    () => new Set(rows.map((r) => r.language_code)),
    [rows]
  );

  /** Languages not yet translated (and never the source language itself). */
  const availableOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    return languageOptions
      .filter((option) => option.value !== sourceLanguageCode)
      .filter((option) => !existingCodes.has(option.value))
      .filter(
        (option) =>
          !term ||
          option.label.toLowerCase().includes(term) ||
          option.value.toLowerCase().includes(term)
      );
  }, [search, sourceLanguageCode, existingCodes]);

  const handleDelete = (languageCode: string) => {
    setDeletingLanguage({
      code: languageCode,
      name: languageLabel(languageCode),
    });
  };

  const handleStart = (languageCode: string, languageName: string) => {
    startMutation.mutate({ language_code: languageCode, language_name: languageName }, {
      onSuccess: () => {
        // Picker stays OPEN — adding languages is usually a run of several, and
        // the one just chosen drops out of the list on its own.
        setSearch('');
      },
    });
  };

  return (
    <div className="space-y-5">
      {/* Intro */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
          <Globe className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">Translations</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Create this course in other languages. Text is translated and audio is
            regenerated with a matching voice — structure, images and settings are kept.
            Always edit content in this original course, then re-translate.
          </p>
        </div>
      </div>

      {/* Bulk stale notice — one click beats several when a master edit
          invalidates every language at once. */}
      {/* Hidden when published, matching the per-row Re-translate buttons: a
          published course's existing translations are not re-run. Adding a NEW
          language is still allowed — that creates rows rather than changing
          content learners are already reading. */}
      {/* Shown on published courses too: refreshing a stale language is a
          repair, and it rewrites only Translated* rows. */}
      {staleCount > 1 && (
        <div className="flex items-center justify-between gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-900">
              {staleCount} languages are out of date — this course changed after they were made.
            </p>
          </div>
          {/* Disabled while any language is still starting. Without this a
              second click re-sent every language and the backend answered 409. */}
          <button
            onClick={() => {
              const staleRows = rows.filter((r) => r.is_stale);
              if (staleRows.length === 0 || retryingCodes.size > 0) return;
              toast.success(`Re-translation started for all selected languages (${staleRows.length})`);
              staleRows.forEach((r) => handleRetry(r.language_code, true));
            }}
            disabled={retryingCodes.size > 0}
            className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-medium hover:bg-amber-700 transition-colors flex-shrink-0 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {retryingCodes.size > 0 && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {retryingCodes.size > 0 ? 'Starting…' : `Re-translate all (${staleCount})`}
          </button>
        </div>
      )}

      {/* Existing translations */}
      {isLoading ? (
        <div className="py-8 flex items-center justify-center">
          <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="py-6 text-center border-2 border-dashed border-gray-200 rounded-lg">
          <p className="text-sm text-gray-500">
            This course has no other language versions yet.
          </p>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
          {rows.map((row) => (
            <TranslationRow
              key={row.language_code}
              row={row}
              isRetrying={retryingCodes.has(row.language_code)}
              isPublished={isPublished}
              onRetry={() => handleRetry(row.language_code)}
              onDelete={() => handleDelete(row.language_code)}
              onOpen={() => onOpenCourse(row.language_code)}
            />
          ))}
        </div>
      )}

      {/* Add a language — NOT on a published course.

          The set of languages a course ships in is part of what was reviewed
          and signed off at publish, so it freezes with the content. The API
          refuses it too (COURSE_ALREADY_PUBLISHED); hiding the button here just
          means the admin never meets that error.

          Repair is a different thing and stays available below — see the
          Re-translate buttons, which are deliberately NOT gated on this. */}
      {!isPublished && (
        <div>
          {!isPickerOpen ? (
            <button
              onClick={() => setIsPickerOpen(true)}
              className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-indigo-300 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-50 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Translate to a new language
            </button>
          ) : (
            <div className="border border-indigo-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-indigo-50 border-b border-indigo-100 flex items-center justify-between gap-3">
                <div className="relative flex-1 max-w-xs">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    autoFocus
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search languages…"
                    className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <button
                  onClick={() => {
                    setIsPickerOpen(false);
                    setSearch('');
                  }}
                  className="p-1.5 text-gray-500 hover:bg-white rounded-lg transition-colors flex-shrink-0"
                  title="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-3 max-h-64 overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {availableOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleStart(option.value, option.label)}
                      disabled={startMutation.isPending}
                      className="flex items-center justify-between px-3 py-2 rounded-lg border border-gray-200 text-left hover:border-indigo-300 hover:bg-indigo-50/60 transition-colors disabled:opacity-50"
                    >
                      <span className="text-sm font-medium text-gray-700 line-clamp-1">
                        {option.label}
                      </span>
                      {startMutation.isPending && startMutation.variables?.language_code === option.value && (
                        <Loader2 className="w-4 h-4 text-indigo-600 animate-spin flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
                {availableOptions.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    {search
                      ? `No languages match “${search}”.`
                      : 'This course has already been translated into every available language.'}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {isPublished && (
        <p className="text-xs text-gray-500">
          This course is published, so its languages are fixed. The ones chosen
          before publishing can still be refreshed or repaired — to add a new
          language, unpublish the course first.
        </p>
      )}

      <DeleteConfirmationModal
        isOpen={!!deletingLanguage}
        onClose={() => setDeletingLanguage(null)}
        onConfirm={() => {
          if (deletingLanguage) {
            deleteMutation.mutate(deletingLanguage.code, {
              onSuccess: () => setDeletingLanguage(null),
            });
          }
        }}
        title="Delete Translation Language"
        itemName={deletingLanguage?.name}
        description="Are you sure you want to delete this translation language version?"
        isLoading={deleteMutation.isPending}
        confirmButtonText="Delete Language"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

interface TranslationRowProps {
  row: CourseTranslationSummary;
  isRetrying: boolean;
  isPublished: boolean;
  onRetry: () => void;
  onDelete: () => void;
  onOpen: () => void;
}

function TranslationRow({
  row,
  isRetrying,
  isPublished,
  onRetry,
  onDelete,
  onOpen,
}: TranslationRowProps) {
  const [showFailures, setShowFailures] = useState(false);

  const isQueued = (row.status === 'PENDING' && !row.started_at) || (row.status as any) === 'QUEUED';
  const running = (row.status === 'PENDING' || row.status === 'IN_PROGRESS') && !isQueued;
  const completed = row.status === 'COMPLETED';
  const hasFailures = row.status === 'PARTIALLY_FAILED' || row.status === 'FAILED';
  // Running far longer than any healthy language does. See looksStalled.
  const stalled = running && looksStalled(row);
  const failedItems = row.failed_items ?? [];

  // How long this language took, or has been taking. Recomputed on every
  // render, and the 4s poll while a run is live makes it tick on its own.
  const timing = describeTranslationTiming(row);

  const p = row.progress;
  // Guarded on the same total the division uses, so the two cannot disagree.
  const progress_total = p.lessons_total + p.audio_total + p.videos_total;
  const percent =
    progress_total > 0
      ? Math.round(
          ((p.lessons_done + p.audio_done + p.videos_done) / progress_total) * 100
        )
      : 0;

  return (
    <div className="px-4 py-3 bg-white">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* Left: language + status */}
        <div className="flex items-center gap-2.5 flex-1 min-w-0 flex-wrap">
          <span className="text-sm font-semibold text-gray-900">
            {languageLabel(row.language_code)}
          </span>

          {isQueued && (
            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium border border-gray-200">
              <Clock className="w-3 h-3 text-gray-400" />
              Pending · queued for translation
            </span>
          )}

          {running && (
            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium border border-blue-200">
              <Loader2 className="w-3 h-3 animate-spin" />
              Translating · module {Math.max(1, p.current_module)}/{p.total_modules}
            </span>
          )}

          {completed && !row.is_stale && (
            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium border border-green-200">
              <CheckCircle2 className="w-3 h-3" />
              Completed{row.translated_at ? ` · ${formatDate(row.translated_at)}` : ''}
            </span>
          )}

          {completed && row.is_stale && (
            <span
              className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-medium border border-amber-200"
              title="This course was edited after this translation was created, so the two no longer match."
            >
              <AlertTriangle className="w-3 h-3" />
              Out of date
            </span>
          )}

          {/* A count alone doesn't tell the admin WHAT to look at — the badge
              expands to name the failed lessons when the backend reports them. */}
          {hasFailures && (
            <button
              onClick={() => failedItems.length > 0 && setShowFailures((s) => !s)}
              disabled={failedItems.length === 0}
              className={`flex items-center gap-1.5 px-2.5 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium border border-red-200 ${
                failedItems.length > 0 ? 'hover:bg-red-100 transition-colors' : 'cursor-default'
              }`}
              title={failedItems.length > 0 ? 'Show which lessons failed' : row.error}
            >
              <AlertTriangle className="w-3 h-3" />
              {row.status === 'FAILED'
                ? 'Failed'
                : `${p.failed_pieces} ${p.failed_pieces === 1 ? 'lesson' : 'lessons'} failed`}
              {failedItems.length > 0 && (
                <ChevronDown
                  className={`w-3 h-3 transition-transform duration-200 ${showFailures ? 'rotate-180' : ''}`}
                />
              )}
            </button>
          )}

          {/* w-full so it wraps onto its own line beneath the badges rather
              than competing with them for horizontal space. Times are what an
              admin needs to answer “how long will the next one take”, which the
              status badge alone never told them. */}
          {timing && (
            <p className="w-full text-xs text-gray-500 mt-0.5">{timing}</p>
          )}
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Repairs are NOT gated on isPublished. A published course whose
              translation has drifted, or whose lessons failed, must still be
              fixable — otherwise publishing would freeze a broken language in
              place with no way back. Only ADDING a language is frozen. */}
          {completed && row.is_stale && (
            <button
              onClick={onRetry}
              disabled={isRetrying}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-medium hover:bg-amber-700 transition-colors disabled:opacity-60"
            >
              {isRetrying ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              Re-translate
            </button>
          )}

          {/* A hung run blocks every language queued behind it and nothing
              recovers it on its own, so it needs a way out of the UI. */}
          {stalled && (
            <button
              onClick={onRetry}
              disabled={isRetrying}
              title="This language has been running unusually long. Restart it to release the queue."
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 transition-colors disabled:opacity-60"
            >
              {isRetrying ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              Restart
            </button>
          )}

          {hasFailures && (
            <button
              onClick={onRetry}
              disabled={isRetrying}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 transition-colors disabled:opacity-60"
            >
              {isRetrying ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              Re-translate failed
            </button>
          )}

          {/* translated_course_id still gates the button: it is only set once
              a clone exists, so a language that has not started has nothing
              to open. It is no longer used to navigate. */}
          {(completed || hasFailures) && row.translated_course_id && (
            <button
              onClick={onOpen}
              className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 transition-colors"
            >
              Open
            </button>
          )}

          {!running && !isPublished && (
            <button
              onClick={onDelete}
              className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
              title="Delete this language version"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Which lessons failed — expanded from the badge above. */}
      {hasFailures && showFailures && failedItems.length > 0 && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50/60 divide-y divide-red-100">
          {failedItems.map((item, index) => (
            <div key={`${item.module}-${item.lesson}-${index}`} className="px-3 py-2">
              <p className="text-xs font-medium text-red-900">{item.lesson}</p>
              <p className="text-[11px] text-red-700 mt-0.5">
                {item.module}
                {item.reason ? ` · ${item.reason}` : ''}
              </p>
            </div>
          ))}
          <p className="px-3 py-2 text-[11px] text-red-700">
            Re-translate failed only refreshes these lessons — the rest are left as they are.
          </p>
        </div>
      )}

      {/* Progress while running */}
      {running && (
        <div className="mt-3">
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
              className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full transition-all duration-700"
              style={{ width: `${percent}%` }}
            ></div>
          </div>
          <div className="flex items-center justify-between mt-1.5">
            {/* The list response carries only a combined piece count — lessons
                plus audio plus assessments — so while a run is in progress it is
                labelled "Pieces". Calling it "Lessons" reported 80 for a
                40-lesson course. Once the detail endpoint has supplied the real
                split (audio_total > 0), the precise breakdown is shown. */}
            {/* A video course counts videos instead of lesson audio — its
                narration is recorded per scene, not per lesson. Videos lag
                behind the text by minutes, so they are worth showing. */}
            <p className="text-xs text-gray-500">
              {p.videos_total > 0
                ? `Lessons ${p.lessons_done}/${p.lessons_total} · Videos ${p.videos_done}/${p.videos_total}`
                : p.audio_total > 0
                  ? `Lessons ${p.lessons_done}/${p.lessons_total} · Audio ${p.audio_done}/${p.audio_total}`
                  : `Pieces ${p.lessons_done}/${p.lessons_total}`}
            </p>
            <p className="text-xs font-medium text-gray-600">{percent}%</p>
          </div>
        </div>
      )}
    </div>
  );
}
